import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Bell, CheckCheck, CircleCheck, Clock, CreditCard, Eye, MailOpen } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import { notificationView } from '../../assets/js/notificationView';
import { notifyNotificationsRead, onNotificationsRead } from '../../assets/js/notificationEvents';
import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import SectionEyebrow from '../common/SectionEyebrow';
import '../../assets/styles/DashboardList.css';
import '../../assets/styles/TemplateBuilder.css';
import '../../assets/styles/ProfileArea.css';
import '../../assets/styles/AdminArea.css';
import '../../assets/styles/Notifications.css';

const API = process.env.REACT_APP_API_URL;
const FILTERS = ['all', 'unread', 'read'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const PAGE_SIZE_KEY = 'tp-notifs-page-size';

const readPageSize = () => {
  try {
    const saved = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZE_OPTIONS.includes(saved) ? saved : PAGE_SIZE_OPTIONS[0];
  } catch (error) {
    return PAGE_SIZE_OPTIONS[0];
  }
};

const NotificationsPage = () => {
  const { t, language } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(readPageSize);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('notif_no_token'));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
      const data = await response.json();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al obtener notificaciones:', err);
      setError(`${t('notif_error_loading')} ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si se lee una desde el desplegable del menu, aqui tambien deja de estar sin leer
  useEffect(() => onNotificationsRead((event) => {
    const ids = (event.detail || {}).ids || [];
    setNotifications((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, is_read: true } : n)));
  }), []);

  const markAsRead = async (ids) => {
    const keys = ids.filter(Boolean);
    if (keys.length === 0) return;

    const before = notifications;
    setNotifications((prev) => prev.map((n) => (keys.includes(n.id) ? { ...n, is_read: true } : n)));
    // El numero del menu lateral baja al momento, sin recargar ni cambiar de pagina
    notifyNotificationsRead(keys);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/notifications/mark-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_keys: keys })
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
    } catch (err) {
      console.error('Error al marcar notificaciones como leídas:', err);
      setNotifications(before);
    }
  };

  const counts = useMemo(() => ({
    all: notifications.length,
    unread: notifications.filter((n) => !n.is_read).length,
    read: notifications.filter((n) => n.is_read).length
  }), [notifications]);

  const visible = useMemo(() => {
    if (filter === 'unread') return notifications.filter((n) => !n.is_read);
    if (filter === 'read') return notifications.filter((n) => n.is_read);
    return notifications;
  }, [notifications, filter]);

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const paged = visible.slice(pageStart, pageStart + pageSize);

  const changePageSize = (size) => {
    setPageSize(size);
    setPage(1);
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch (err) {
      console.error('No se pudo guardar el tamaño de página:', err);
    }
  };

  const filterLabel = {
    all: t('notif_filter_all'),
    unread: t('notif_filter_unread'),
    read: t('notif_filter_read')
  };


  if (loading) {
    return (
      <div className="home-container tp-dash tp-admin tp-notifs">
        <div className="dashboard-container tp-admin--center">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="home-container tp-dash tp-admin tp-notifs">
      <div className="dashboard-container">
        <header className="tp-dash__head">
          <div className="tp-dash__heading">
            <SectionEyebrow>{t('adminheader_notifications')}</SectionEyebrow>
            <p className="tp-dash__lead">{t('notif_lead')}</p>
          </div>
          {counts.unread > 0 && (
            <button
              type="button"
              className="btn btn--outline tp-dash__new"
              onClick={() => markAsRead(notifications.filter((n) => !n.is_read).map((n) => n.id))}
            >
              <CheckCheck size={16} strokeWidth={2.25} aria-hidden="true" />
              {t('notif_mark_all')}
            </button>
          )}
        </header>

        {error && <p className="tp-form__alert" role="alert">{error}</p>}

        <div className="tp-dash__toolbar">
          <div className="tp-dash__tabs" role="group" aria-label={t('notif_filter_label')}>
            {FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                className={`tp-dash__tab ${filter === key ? 'is-active' : ''}`.trim()}
                aria-pressed={filter === key}
                onClick={() => { setFilter(key); setPage(1); }}
              >
                {filterLabel[key]}
                <span className="tp-dash__tab-count">{counts[key]}</span>
              </button>
            ))}
          </div>
        </div>

        {notifications.length === 0 && (
          <div className="tp-dash__empty">
            <span className="tp-dash__empty-icon" aria-hidden="true">
              <Bell size={26} strokeWidth={1.75} />
            </span>
            <p className="tp-dash__empty-title">{t('notif_no_notifications')}</p>
            <p className="tp-dash__empty-text">{t('notif_empty_text')}</p>
          </div>
        )}

        {notifications.length > 0 && visible.length === 0 && (
          <p className="tp-dash__no-match">{t('notif_no_match')}</p>
        )}

        {paged.length > 0 && (
          <ul className="tp-dash__list">
            {paged.map((notification) => {
              const view = notificationView(notification, t, language);
              return (
              <li
                key={notification.id}
                className={`tp-dash__item tp-notifs__item ${notification.is_read ? '' : 'is-unread'}`.trim()}
              >
                <span className={`tp-notifs__icon is-${view.kind}`} aria-hidden="true">
                  {view.kind === 'plan' && <Clock size={18} strokeWidth={2} />}
                  {view.kind === 'survey' && <BarChart3 size={18} strokeWidth={2} />}
                  {view.kind === 'done' && <CircleCheck size={18} strokeWidth={2} />}
                </span>

                <div className="tp-dash__main">
                  <div className="tp-dash__title-row">
                    <h3 className="tp-dash__title">{view.title}</h3>
                    {!notification.is_read && (
                      <span className="tp-notifs__badge">{t('notif_unread')}</span>
                    )}
                  </div>
                  <p className="tp-notifs__date">
                    {new Date(notification.date_event).toLocaleString()}
                  </p>
                </div>

                <div className="tp-dash__actions">
                  {view.href && (
                    <Link
                      to={view.href}
                      className="btn btn--outline btn--sm tp-dash__primary"
                      onClick={() => { if (!notification.is_read) markAsRead([notification.id]); }}
                    >
                      {view.kind === 'plan'
                        ? <CreditCard size={15} strokeWidth={2} aria-hidden="true" />
                        : <Eye size={15} strokeWidth={2} aria-hidden="true" />}
                      {view.action}
                    </Link>
                  )}
                  {!notification.is_read && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm tp-notifs__read"
                      onClick={() => markAsRead([notification.id])}
                    >
                      <MailOpen size={15} strokeWidth={2} aria-hidden="true" />
                      {t('notif_mark_read')}
                    </button>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
        )}

        {visible.length > 0 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onChange={setPage}
            total={visible.length}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={changePageSize}
            summary={t('dash_page_summary')
              .replace('{from}', pageStart + 1)
              .replace('{to}', pageStart + paged.length)
              .replace('{total}', visible.length)}
            labels={{
              nav: t('dash_pagination_label'),
              prev: t('dash_page_prev'),
              next: t('dash_page_next'),
              page: (n) => t('dash_page_n').replace('{n}', n),
              show: t('dash_page_show'),
              perPage: t('dash_page_per_page'),
              perPageLabel: t('notif_page_size_label')
            }}
          />
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
